export interface ProjectsResponse {
  status: string;
  data: Project[];
}

export interface ProjectResponse {
  status: string;
  data: Project;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  status: ProjectStatus;
  requirementType: RequirementType;
  figmaDesignUrl?: string;
  figmaDesignData?: string;
  figmaDesignDataUrl?: string;
  approvals?: ProjectApprovals;
  artifacts: Artifacts;
}

export interface ProjectApprovals {
  requirements?: boolean;
  jiraStories?: boolean;
  diagrams?: boolean;
  database?: boolean;
  code?: boolean;
}

export enum ProjectStatus {
  PROCESSING_REQUIREMENT = 'PROCESSING_REQUIREMENT',
  GENERATING_ARTIFACTS = 'GENERATING_ARTIFACTS',
  GENERATING_CODE = 'GENERATING_CODE',
  VALIDATING = 'VALIDATING',
  DEPLOYING = 'DEPLOYING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum RequirementType {
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
  FIGMA_JSON = 'FIGMA_JSON',
  RULES = 'RULES'
}

export interface Artifacts {
  requirementDoc?: string;
  requirementDocUrl?: string;
  jiraStories?: JiraStory[] | JiraStoriesContent;
  jiraStoriesEditedTimestamp?: string;
  jiraLoginUrl?: string;
  diagrams?: Diagram[];
  code?: CodeArtifact;
  designDocument?: string;
  designDocumentUrl?: string;
  designDocumentS3Key?: string;
  confluencePageUrl?: string;
  deployedUrl?: string;
}

export interface JiraStoriesContent {
  artifact_id?: string;
  title?: string;
  constraints?: string[];
  content?: {
    epics?: Array<any>;
    sprint_planning?: Array<any>;
    stories?: Array<{
      story_id: string;
      title: string;
      description: string;
      acceptance_criteria: string[];
      story_points: number;
      priority: string;
      linked_requirement_id?: string[];
      linked_epic_id?: string;
      assumptions?: string[];
      dependencies?: string[];
    }>;
  };
  assumptions?: string[];
}

export interface JiraStory {
  id?: string;
  story_id?: string;
  title: string;
  userStory: string;
  url?: string;
  storyPoints?: number;
  acceptanceCriteria?: string[] | string;
  definition_of_done?: string[];  // Added to support new DoD feature
  priority?: string;
  linkedRequirementId?: string[];
  linkedEpicId?: string;
  assumptions?: string[];
  dependencies?: string[];
}

export interface Diagram {
  id: string;
  name: string;
  type: DiagramType;
  url?: string;
  body?: string;
  explanation?: string;
  description?: string;
  requirement_mapping?: string[];  // Added to support requirement tracing
  lastModified?: string;  // Timestamp when diagram was last edited
}

export enum DiagramType {
  HIGH_LEVEL_DESIGN = 'HIGH_LEVEL_DESIGN',
  LOW_LEVEL_DESIGN = 'LOW_LEVEL_DESIGN',
  DATA_FLOW = 'DATA_FLOW',
  UML = 'UML',
  API = 'API'
}

export interface CodeArtifact {
  frontend?: string;
  backend?: string;
  database?: string;
  infrastructure?: string;
  repositoryUrl?: string;
  deploymentUrl?: string;
  fullstackRepositoryUrl?: string;
  fullstackEditedCodeRepoUrl?: string;
  fullstackEditedCode?: string;
  frontendRepositoryUrl?: string;
  backendRepositoryUrl?: string;
  databaseRepositoryUrl?: string;
  terraformRepositoryUrl?: string;
  // Database raw text fields
  databaseRawText?: string;
  databaseRawTextDownloadUrl?: string;
  isEdited?: boolean;
  editedTimestamp?: string;
  databaseIsEdited?: boolean;
  databaseEditedTimestamp?: string;
  needsContinuation?: boolean;
  techStack?: string;
  testcaseDownloadUrl?: string;
}

export interface AIAnalysisRequest {}

export interface AIAnalysisResponse {}

export interface Requirement {}

export interface RequirementUploadResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    fileKey: string;
  };
}

export interface GenerateJiraStoriesResponse {
  status: string;
  data: {
    jiraStories: JiraStory[];
  };
}

export interface GenerateDiagramsResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    diagrams: Diagram[];
  };
}

export interface GenerateCodeResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    code: CodeArtifact;
  };
}

export interface GenerateDeploymentResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    deploymentUrl: string;
  };
}

export interface GenerateInfrastructureResponse {
  status: string;
  message: string;
  data: {
    terraformInfrastructure: any;
    terraformRepositoryUrl: string;
    terraformCode: string;
  };
}

export interface GenerateTestCasesResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    testCases: TestCase[];
  };
}

export interface TestCase {
  id: string;
  title: string;
  description: string;
  steps: string[];
  expectedResult: string;
  actualResult?: string;
  status?: TestCaseStatus;
}

export interface TestCaseUploadResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    testCaseId: string;
  };
}

export interface TestCaseExecutionResponse {
  status: string;
  message: string;
  data: {
    projectId: string;
    requirementType: string;
    testCaseId: string;
    executionResult: TestCaseExecutionResult;
  };
}

export interface TestCaseExecutionResult {
  id: string;
  testCaseId: string;
  status: TestCaseStatus;
  executionTime: number; // in milliseconds
  errorMessage?: string;
}

export enum TestCaseStatus {
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  BLOCKED = 'BLOCKED',
  NOT_EXECUTED = 'NOT_EXECUTED'
}

export interface RequirementsTextResponse {
  status: string;
  data: {
    requirementsDoc: string;
  };
}

/**
 * Image input for multimodal requirements generation
 */
export interface ImageInput {
  base64Data: string;
  mediaType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
}

/**
 * Request payload for generating requirements document with optional images
 */
export interface GenerateRequirementsRequest {
  rawText: string;
  projectId?: string;
  images?: ImageInput[];
}

/**
 * Response from requirements document generation
 */
export interface GenerateRequirementsResponse {
  status: string;
  data: {
    requirementsDoc: string;
  };
}